<template>
	<div class="main-content">
		<!-- HEADER -->
		<div class="header">
			<div class="container-fluid">
				<div class="card mt-5">
					<div class="card-body">
						<div class="row">
							<div class="col-12 col-md-4">
								<div class="d-flex flex-column gap-2">
									<h2 class="text-left">Budget Compare</h2>
									<div class="d-md-flex align-items-center justify-content-center justify-content-md-start">
										<span class="mr-2">{{ detail.year || '' }} - {{ detail.title || '-' }}</span>
										<span class="mx-2">-</span>
										<span class="ml-2 px-3 py-1 border border-primary rounded"> Version {{ detail.version || 'N/A' }} </span>
									</div>
									<span class="text-left font-weight-bold mt-n2">{{ detail.departments }}</span>
								</div>
							</div>
							<div class="col-12 col-md-8">
								<div class="row">
									<div class="col-12 col-md-4">
										<div class="d-flex flex-column gap-1 text-left">
											<label>Compare With Department</label>
											<v-select
												:options="listDepartement"
												v-model="selectedDepartment"
												:reduce="(e) => e.value"
												placeholder="Select/Type Here..."
												class="w-md-auto"
												@input="selectFundCenter"
											/>
										</div>
									</div>
									<div class="col-12 col-md-4">
										<!-- Compare With Year -->
										<div class="d-flex flex-column gap-1 text-left">
											<label>Compare With Year</label>
											<v-select
												:options="yearOptions"
												v-model="selectedYear"
												:reduce="(e) => e.value"
												placeholder="Select/Type Here..."
												class="w-md-auto"
												@input="fetchVersions"
												:disabled="!selectedDepartment"
											/>
										</div>
									</div>
									<div class="col-12 col-md-4">
										<!-- Compare With Version -->
										<div class="d-flex flex-column gap-1 text-left">
											<label>Compare With Version</label>
											<v-select
												label="version"
												:options="formattedVersionOptions"
												:clearable="false"
												:reduce="(e) => e.id"
												v-model="comparedId"
												@input="getCompare()"
												placeholder="Select/Type Here..."
												class="w-md-auto"
												:disabled="!selectedYear || !selectedDepartment"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<!-- Body -->
			</div>
		</div>
		<!-- / .header -->

		<div class="container-fluid" v-if="isLoading == true">
			<div class="card">
				<div class="card-body">
					<b-skeleton-table :rows="7" :columns="3" :table-props="{ bordered: true, striped: true }"></b-skeleton-table>
				</div>
			</div>

			<div class="card">
				<div class="card-body">
					<b-skeleton-table :rows="2" :columns="3" :table-props="{ bordered: true, striped: true }"></b-skeleton-table>
				</div>
			</div>
		</div>

		<div class="container-fluid" v-if="comparedId && isLoading == false">
			<div class="card">
				<div class="card-body">
					<table class="table table-sm table-bordered p-0">
						<thead>
							<tr>
								<th rowspan="2" class="text-center"></th>
								<th rowspan="2" class="text-center">Version {{ compared.version.v1 }} - {{ compared.year.v1 }}</th>
								<th rowspan="2" class="text-center">Version {{ compared.version.v2 }} - {{ compared.year.v2 }}</th>
								<th colspan="2" class="text-center">Deviation</th>
							</tr>
							<tr>
								<th class="text-center">Nominal</th>
								<th class="text-center">Percentage</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>Title</td>
								<td>{{ compared.title.v1 }}</td>
								<td>{{ compared.title.v2 }}</td>
								<td></td>
								<td></td>
							</tr>
							<tr>
								<td>Year</td>
								<td>{{ compared.year.v1 }}</td>
								<td>{{ compared.year.v2 }}</td>
								<td></td>
								<td></td>
							</tr>
							<tr>
								<td>Status</td>
								<td>
									<span :class="`badge bg-${compared.is_active.v1 === true ? 'success' : 'danger'}-soft`">{{
										compared.is_active.v1 === true ? 'ACTIVE' : 'INACTIVE'
									}}</span>
								</td>
								<td>
									<span :class="`badge bg-${compared.is_active.v2 === true ? 'success' : 'danger'}-soft`">{{
										compared.is_active.v2 === true ? 'ACTIVE' : 'INACTIVE'
									}}</span>
								</td>
								<td></td>
								<td></td>
							</tr>
							<tr>
								<td>Fund Center</td>
								<td>{{ compared.fund_center.v1.fund_center }}</td>
								<td>{{ compared.fund_center.v2.fund_center }}</td>
								<td></td>
								<td></td>
							</tr>
							<tr>
								<td>Opex Total</td>
								<td>Rp. {{ convertNumber(compared.opexTotal.v1) }}</td>
								<td>Rp. {{ convertNumber(compared.opexTotal.v2) }}</td>
								<td>Rp. {{ convertNumber(compared.opexTotal.v2 - compared.opexTotal.v1) }}</td>
								<td>
									{{
										parseFloat(compared.opexTotal.v1) > 0
											? Math.round(
													((parseFloat(compared.opexTotal.v2) - parseFloat(compared.opexTotal.v1)) /
														parseFloat(compared.opexTotal.v1)) *
														100
											  )
											: parseFloat(compared.opexTotal.v2) > 0
											? 100
											: 0
									}}
									%
								</td>
							</tr>
							<tr>
								<td>Capex Total</td>
								<td>Rp. {{ convertNumber(compared.capexTotal.v1) }}</td>
								<td>Rp. {{ convertNumber(compared.capexTotal.v2) }}</td>
								<td>Rp. {{ convertNumber(compared.capexTotal.v2 - compared.capexTotal.v1) }}</td>
								<td>
									{{
										parseFloat(compared.capexTotal.v1) > 0
											? Math.round(
													((parseFloat(compared.capexTotal.v2) - parseFloat(compared.capexTotal.v1)) /
														parseFloat(compared.capexTotal.v1)) *
														100,
													2
											  )
											: parseFloat(compared.capexTotal.v2) > 0
											? 100
											: 0
									}}
									%
								</td>
							</tr>
							<tr>
								<td>Total Budget</td>
								<td>Rp. {{ convertNumber(compared.total.v1) }}</td>
								<td>Rp. {{ convertNumber(compared.total.v2) }}</td>
								<td>Rp. {{ convertNumber(compared.total.v2 - compared.total.v1) }}</td>
								<td>
									{{
										parseFloat(compared.total.v1) > 0
											? Math.round(
													((parseFloat(compared.total.v2) - parseFloat(compared.total.v1)) / parseFloat(compared.total.v1)) * 100,
													2
											  )
											: parseFloat(compared.total.v2) > 0
											? 100
											: 0
									}}
									%
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
			<div class="row">
				<div class="col-12">
					<!-- {{ compared }} -->
					<div class="card">
						<b-tabs pills card fill>
							<b-tab @click="openOpex()" :title="`OPEX`" :active="activeCapex == false ? true : false">
								<div>
									<div class="row">
										<div class="col-lg-5">
											<div class="form-group">
												<label>Transaction Type</label>
												<v-select
													:clearable="false"
													v-model="TTmodel"
													:options="compared.opex"
													:reduce="(e) => e.account_id"
													@input="selectTransactionOpex($event, compared.opex)"
												>
													<template slot="option" slot-scope="option">
														{{ option.account.account }}
													</template>

													<template slot="selected-option" slot-scope="option">
														{{ option.account.account }}
													</template></v-select
												>
											</div>
										</div>
										<div class="col-lg-5">
											<label>Budget Item</label>
											<v-select
												:clearable="false"
												:disabled="TTmodel ? false : true"
												v-model="budgetItemOpex"
												:reduce="(e) => e.opex_gl?.gl_code"
												:options="budgetItemList.opex_item"
												multiple
											>
												<template slot="option" slot-scope="option">
													{{ option.opex_gl?.budgetItem?.budget_item }}
												</template>

												<template slot="selected-option" slot-scope="option">
													{{ option.opex_gl?.budgetItem?.budget_item }}
												</template>
											</v-select>
										</div>
										<div class="col-lg-2">
											<b-button
												variant="primary"
												:disabled="budgetItemOpex.length ? false : true"
												class="mt-4"
												@click="selectBudgetItemOpex"
												>Filter</b-button
											>
											<b-button variant="primary" class="ml-3 mt-4" @click="resetFilter">Reset Filter</b-button>
										</div>
									</div>
								</div>
								<div class="table-budget-container">
									<table v-if="filtering == false" class="table table-sm p-0 table-sticky">
										<thead>
											<tr>
												<th class="text-center sticky-header-1" style="width: 150px" rowspan="3">GL</th>
												<th class="text-center sticky-header-2" style="width: 100px" rowspan="3">TT</th>
												<th class="text-center sticky-header-3" style="width: 350px" rowspan="3">Budget Item</th>
												<th class="text-center" style="width: 700px" colspan="4">January</th>
												<th class="text-center" style="width: 700px" colspan="4">February</th>
												<th class="text-center" style="width: 700px" colspan="4">March</th>
												<th class="text-center" style="width: 700px" colspan="4">April</th>
												<th class="text-center" style="width: 700px" colspan="4">May</th>
												<th class="text-center" style="width: 700px" colspan="4">June</th>
												<th class="text-center" style="width: 700px" colspan="4">July</th>
												<th class="text-center" style="width: 700px" colspan="4">August</th>
												<th class="text-center" style="width: 700px" colspan="4">September</th>
												<th class="text-center" style="width: 700px" colspan="4">October</th>
												<th class="text-center" style="width: 700px" colspan="4">November</th>
												<th class="text-center" style="width: 700px" colspan="4">December</th>
												<th class="text-center" style="width: 700px" colspan="4">Total</th>
												<th class="text-center" style="width: 350px" colspan="2">Savings</th>
											</tr>
											<tr>
												<template v-for="month in months">
													<th class="text-center" style="width: 250px" rowspan="2">
														Version {{ compared.version.v1 }} - {{ compared.year.v1 }}
													</th>
													<th class="text-center" style="width: 250px" rowspan="2">
														Version {{ compared.version.v2 }} - {{ compared.year.v2 }}
													</th>
													<th class="text-center" style="width: 250px" colspan="2">Deviation</th>
												</template>

												<th class="text-center" style="width: 250px" rowspan="2">
													Version {{ compared.version.v1 }} - {{ compared.year.v1 }}
												</th>
												<th class="text-center" style="width: 250px" rowspan="2">
													Version {{ compared.version.v2 }} - {{ compared.year.v2 }}
												</th>
												<th class="text-center" style="width: 250px" colspan="2">Deviation</th>
												<th class="text-center" style="width: 350px" rowspan="2">Nominal</th>
												<th class="text-center" style="width: 350px" rowspan="2">Percentage</th>
											</tr>
											<tr>
												<template v-for="month in months">
													<th class="text-center" style="width: 350px">Nominal</th>
													<th class="text-center" style="width: 350px">Percentage</th>
												</template>
												<th class="text-center" style="width: 350px">Nominal</th>
												<th class="text-center" style="width: 350px">Percentage</th>
											</tr>
										</thead>
										<tbody v-for="(dat, index) in compared.opex" :key="index">
											<tr class="opex-first">
												<td class="sticky-column-1"></td>
												<td class="bg-gray sticky-column-2">{{ dat.account.code }}</td>
												<td class="bg-gray sticky-column-3">{{ dat.account.account }}</td>
												<template v-for="month in months">
													<td class="bg-gray">Rp. {{ convertNumber(dat.compare[month].v1) }}</td>
													<td class="bg-gray">Rp. {{ convertNumber(dat.compare[month].v2) }}</td>
													<td class="bg-gray">Rp. {{ convertNumber(dat.compare[month].v2 - dat.compare[month].v1) }}</td>
													<td class="bg-gray">
														{{
															parseFloat(dat.compare[month].v1) > 0
																? Math.round(
																		((parseFloat(dat.compare[month].v2) - parseFloat(dat.compare[month].v1)) /
																			parseFloat(dat.compare[month].v1)) *
																			100
																  )
																: parseFloat(dat.compare[month].v2) > 0
																? 100
																: 0
														}}
														%
													</td>
												</template>
												<td class="bg-gray">Rp. {{ convertNumber(dat.compare.total.v1) }}</td>
												<td class="bg-gray">Rp. {{ convertNumber(dat.compare.total.v2) }}</td>
												<td class="bg-gray">Rp. {{ convertNumber(dat.compare.total.v2 - dat.compare.total.v1) }}</td>
												<td class="bg-gray">
													{{
														parseFloat(dat.compare.total.v1) > 0
															? Math.round(
																	((parseFloat(dat.compare.total.v2) - parseFloat(dat.compare.total.v1)) /
																		parseFloat(dat.compare.total.v1)) *
																		100
															  )
															: parseFloat(dat.compare.total.v2) > 0
															? 100
															: 0
													}}
													%
												</td>
												<td class="bg-gray">
													Rp.
													{{
														parseFloat(dat.compare.total.v1) > parseFloat(dat.compare.total.v2)
															? convertNumber(dat.compare.total.v1 - dat.compare.total.v2)
															: 0
													}}
												</td>
												<td class="bg-gray">
													{{
														parseFloat(dat.compare.total.v1) > parseFloat(dat.compare.total.v2)
															? Math.round(
																	((parseFloat(dat.compare.total.v1) - parseFloat(dat.compare.total.v2)) /
																		parseFloat(dat.compare.total.v1)) *
																		100
															  )
															: 0
													}}
													%
												</td>
											</tr>

											<tr v-for="(list, indx) in dat.opex_item" :key="indx" class="opex">
												<td class="sticky-column-1">{{ list.opex_gl.gl_code }}</td>
												<td class="sticky-column-2">{{ list.opex_gl.budgetItem.item }}</td>
												<td
													v-b-popover.hover.top="'Click here for more detail'"
													style="cursor: pointer"
													@click="detailBudget(list)"
													class="sticky-column-3"
												>
													{{ list.opex_gl.budgetItem.budget_item }} <span class="float-end fe fe-maximize-2"></span>
												</td>
												<template v-for="month in months">
													<td>Rp. {{ convertNumber(list.compare[month].v1) }}</td>
													<td>Rp. {{ convertNumber(list.compare[month].v2) }}</td>
													<td>Rp. {{ convertNumber(list.compare[month].v2 - list.compare[month].v1) }}</td>
													<td>
														{{
															parseFloat(list.compare[month].v1) > 0
																? Math.round(
																		((parseFloat(list.compare[month].v2) - parseFloat(list.compare[month].v1)) /
																			parseFloat(list.compare[month].v1)) *
																			100
																  )
																: parseFloat(list.compare[month].v2) > 0
																? 100
																: 0
														}}
														%
													</td>
												</template>
												<td>Rp. {{ convertNumber(list.compare.total.v1) }}</td>
												<td>Rp. {{ convertNumber(list.compare.total.v2) }}</td>
												<td>Rp. {{ convertNumber(list.compare.total.v2 - list.compare.total.v1) }}</td>
												<td>
													{{
														parseFloat(list.compare.total.v1) > 0
															? Math.round(
																	((parseFloat(list.compare.total.v2) - parseFloat(list.compare.total.v1)) /
																		parseFloat(list.compare.total.v1)) *
																		100
															  )
															: parseFloat(list.compare.total.v2) > 0
															? 100
															: 0
													}}
													%
												</td>
												<td>
													Rp.
													{{
														parseFloat(list.compare.total.v1) > parseFloat(list.compare.total.v2)
															? convertNumber(list.compare.total.v1 - list.compare.total.v2)
															: 0
													}}
												</td>
												<td>
													{{
														parseFloat(list.compare.total.v1) > parseFloat(list.compare.total.v2)
															? Math.round(
																	((parseFloat(list.compare.total.v1) - parseFloat(list.compare.total.v2)) /
																		parseFloat(list.compare.total.v1)) *
																		100
															  )
															: 0
													}}
													%
												</td>
											</tr>
										</tbody>
									</table>
									<table v-if="filtering == true" class="table table-sm p-0 table-sticky">
										<thead>
											<tr>
												<th class="text-center sticky-header-1" style="width: 150px" rowspan="3">GL</th>
												<th class="text-center sticky-header-2" style="width: 100px" rowspan="3">TT</th>
												<th class="text-center sticky-header-3" style="width: 350px" rowspan="3">Budget Item</th>
												<th class="text-center" style="width: 700px" colspan="4">January</th>
												<th class="text-center" style="width: 700px" colspan="4">February</th>
												<th class="text-center" style="width: 700px" colspan="4">March</th>
												<th class="text-center" style="width: 700px" colspan="4">April</th>
												<th class="text-center" style="width: 700px" colspan="4">May</th>
												<th class="text-center" style="width: 700px" colspan="4">June</th>
												<th class="text-center" style="width: 700px" colspan="4">July</th>
												<th class="text-center" style="width: 700px" colspan="4">August</th>
												<th class="text-center" style="width: 700px" colspan="4">September</th>
												<th class="text-center" style="width: 700px" colspan="4">October</th>
												<th class="text-center" style="width: 700px" colspan="4">November</th>
												<th class="text-center" style="width: 700px" colspan="4">December</th>
												<th class="text-center" style="width: 700px" colspan="4">Total</th>
												<th class="text-center" style="width: 350px" colspan="2">Savings</th>
											</tr>
											<tr>
												<template v-for="month in months">
													<th class="text-center" style="width: 250px" rowspan="2">
														Version {{ compared.version.v1 }} - {{ compared.year.v1 }}
													</th>
													<th class="text-center" style="width: 250px" rowspan="2">
														Version {{ compared.version.v2 }} - {{ compared.year.v2 }}
													</th>
													<th class="text-center" style="width: 250px" colspan="2">Deviation</th>
												</template>

												<th class="text-center" style="width: 250px" rowspan="2">
													Version {{ compared.version.v1 }} - {{ compared.year.v1 }}
												</th>
												<th class="text-center" style="width: 250px" rowspan="2">
													Version {{ compared.version.v2 }} - {{ compared.year.v2 }}
												</th>
												<th class="text-center" style="width: 250px" colspan="2">Deviation</th>
												<th class="text-center" style="width: 350px" rowspan="2">Nominal</th>
												<th class="text-center" style="width: 350px" rowspan="2">Percentage</th>
											</tr>
											<tr>
												<template v-for="month in months">
													<th class="text-center" style="width: 350px">Nominal</th>
													<th class="text-center" style="width: 350px">Percentage</th>
												</template>
												<th class="text-center" style="width: 350px">Nominal</th>
												<th class="text-center" style="width: 350px">Percentage</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="(list, indx) in comparedFilter" :key="indx" class="opex">
												<td class="sticky-column-1">{{ list.opex_gl.gl_code }}</td>
												<td class="sticky-column-2">{{ list.opex_gl.budgetItem.item }}</td>
												<td
													v-b-popover.hover.top="'Click here for more detail'"
													style="cursor: pointer"
													@click="detailBudget(list)"
													class="sticky-column-3"
												>
													{{ list.opex_gl.budgetItem.budget_item }} <span class="float-end fe fe-maximize-2"></span>
												</td>
												<template v-for="month in months">
													<td>Rp. {{ convertNumber(list.compare[month].v1) }}</td>
													<td>Rp. {{ convertNumber(list.compare[month].v2) }}</td>
													<td>Rp. {{ convertNumber(list.compare[month].v2 - list.compare[month].v1) }}</td>
													<td>
														{{
															parseFloat(list.compare[month].v1) > 0
																? Math.round(
																		((parseFloat(list.compare[month].v2) - parseFloat(list.compare[month].v1)) /
																			parseFloat(list.compare[month].v1)) *
																			100
																  )
																: parseFloat(list.compare[month].v2) > 0
																? 100
																: 0
														}}
														%
													</td>
												</template>
												<td>Rp. {{ convertNumber(list.compare.total.v1) }}</td>
												<td>Rp. {{ convertNumber(list.compare.total.v2) }}</td>
												<td>Rp. {{ convertNumber(list.compare.total.v2 - list.compare.total.v1) }}</td>
												<td>
													{{
														parseFloat(list.compare.total.v1) > 0
															? Math.round(
																	((parseFloat(list.compare.total.v2) - parseFloat(list.compare.total.v1)) /
																		parseFloat(list.compare.total.v1)) *
																		100
															  )
															: parseFloat(list.compare.total.v2) > 0
															? 100
															: 0
													}}
													%
												</td>
												<td>
													Rp.
													{{
														parseFloat(list.compare.total.v1) > parseFloat(list.compare.total.v2)
															? convertNumber(list.compare.total.v1 - list.compare.total.v2)
															: 0
													}}
												</td>
												<td>
													{{
														parseFloat(list.compare.total.v1) > parseFloat(list.compare.total.v2)
															? Math.round(
																	((parseFloat(list.compare.total.v1) - parseFloat(list.compare.total.v2)) /
																		parseFloat(list.compare.total.v1)) *
																		100
															  )
															: 0
													}}
													%
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</b-tab>
							<b-tab @click="openCapex()" :title="`CAPEX`" :active="activeCapex == true ? true : false">
								<div class="row mb-3">
									<div class="col-lg-10">
										<label>Budget Item</label>
										<v-select
											:options="compared.capex"
											multiple
											v-model="budgetItemCapex"
											:clearable="false"
											:reduce="(e) => e.capex_gl.gl_code"
										>
											<template slot="option" slot-scope="option">
												{{ option.capex_gl.account.account }}
											</template>

											<template slot="selected-option" slot-scope="option">
												{{ option.capex_gl.account.account }}
											</template></v-select
										>
									</div>
									<div class="col-lg-2">
										<b-button
											variant="primary"
											:disabled="budgetItemCapex.length ? false : true"
											class="mt-4"
											@click="capexFilter"
											>Filter</b-button
										>
										<b-button variant="primary" class="ml-3 mt-4" @click="resetFilter">Reset Filter</b-button>
									</div>
								</div>
								<div class="table-budget-container">
									<table v-if="filteringCapex == false" class="table table-sm p-0 table-sticky">
										<thead>
											<tr>
												<th class="text-center sticky-header-1" style="width: 150px" rowspan="3">GL</th>
												<th class="text-center sticky-header-2" style="width: 100px" rowspan="3">TT</th>
												<th class="text-center sticky-header-3" style="width: 350px" rowspan="3">Budget Item</th>
												<th class="text-center" style="width: 350px" rowspan="3">Description</th>
												<th class="text-center" style="width: 700px" colspan="4">January</th>
												<th class="text-center" style="width: 700px" colspan="4">February</th>
												<th class="text-center" style="width: 700px" colspan="4">March</th>
												<th class="text-center" style="width: 700px" colspan="4">April</th>
												<th class="text-center" style="width: 700px" colspan="4">May</th>
												<th class="text-center" style="width: 700px" colspan="4">June</th>
												<th class="text-center" style="width: 700px" colspan="4">July</th>
												<th class="text-center" style="width: 700px" colspan="4">August</th>
												<th class="text-center" style="width: 700px" colspan="4">September</th>
												<th class="text-center" style="width: 700px" colspan="4">October</th>
												<th class="text-center" style="width: 700px" colspan="4">November</th>
												<th class="text-center" style="width: 700px" colspan="4">December</th>
												<th class="text-center" style="width: 700px" colspan="4">Total</th>
												<th class="text-center" style="width: 350px" colspan="2">Savings</th>
											</tr>
											<tr>
												<template v-for="month in months">
													<th class="text-center" style="width: 250px" rowspan="2">
														Version {{ compared.version.v1 }} - {{ compared.year.v1 }}
													</th>
													<th class="text-center" style="width: 250px" rowspan="2">
														Version {{ compared.version.v2 }} - {{ compared.year.v2 }}
													</th>
													<th class="text-center" style="width: 250px" colspan="2">Deviation</th>
												</template>

												<th class="text-center" style="width: 250px" rowspan="2">
													Version {{ compared.version.v1 }} - {{ compared.year.v1 }}
												</th>
												<th class="text-center" style="width: 250px" rowspan="2">
													Version {{ compared.version.v2 }} - {{ compared.year.v2 }}
												</th>
												<th class="text-center" style="width: 250px" colspan="2">Deviation</th>
												<th class="text-center" style="width: 350px" rowspan="2">Nominal</th>
												<th class="text-center" style="width: 350px" rowspan="2">Percentage</th>
											</tr>
											<tr>
												<template v-for="month in months">
													<th class="text-center" style="width: 350px">Nominal</th>
													<th class="text-center" style="width: 350px">Percentage</th>
												</template>
												<th class="text-center" style="width: 350px">Nominal</th>
												<th class="text-center" style="width: 350px">Percentage</th>
											</tr>
										</thead>
										<tbody v-for="(dat, index) in compared.capex" :key="index">
											<tr class="opex-first">
												<td class="bg-gray sticky-column-1">{{ dat.capex_gl.gl_code }}</td>
												<td class="bg-gray sticky-column-2">{{ dat.capex_gl.account.code }}</td>
												<td class="bg-gray sticky-column-3">{{ dat.capex_gl.account.account }}</td>
												<td class="bg-gray"></td>
												<template v-for="month in months">
													<td class="bg-gray">Rp. {{ convertNumber(dat.compare[month].v1) }}</td>
													<td class="bg-gray">Rp. {{ convertNumber(dat.compare[month].v2) }}</td>
													<td class="bg-gray">Rp. {{ convertNumber(dat.compare[month].v2 - dat.compare[month].v1) }}</td>
													<td class="bg-gray">
														{{
															parseFloat(dat.compare.total.v1) > 0
																? Math.round(
																		((parseFloat(dat.compare.total.v2) - parseFloat(dat.compare.total.v1)) /
																			parseFloat(dat.compare.total.v1)) *
																			100
																  )
																: parseFloat(dat.compare.total.v2) > 0
																? 100
																: 0
														}}
														%
													</td>
												</template>
												<td class="bg-gray">Rp. {{ convertNumber(dat.compare.total.v1) }}</td>
												<td class="bg-gray">Rp. {{ convertNumber(dat.compare.total.v2) }}</td>
												<td class="bg-gray">Rp. {{ convertNumber(dat.compare.total.v2 - dat.compare.total.v1) }}</td>
												<td class="bg-gray">
													{{
														parseFloat(dat.compare.total.v1) > 0
															? Math.round(
																	((parseFloat(dat.compare.total.v2) - parseFloat(dat.compare.total.v1)) /
																		parseFloat(dat.compare.total.v1)) *
																		100
															  )
															: parseFloat(dat.compare.total.v2) > 0
															? 100
															: 0
													}}
													%
												</td>
												<td class="bg-gray">
													Rp.
													{{
														parseFloat(dat.compare.total.v1) > parseFloat(dat.compare.total.v2)
															? convertNumber(dat.compare.total.v1 - dat.compare.total.v2)
															: 0
													}}
												</td>
												<td class="bg-gray">
													{{
														parseFloat(dat.compare.total.v1) > parseFloat(dat.compare.total.v2)
															? Math.round(
																	((parseFloat(dat.compare.total.v1) - parseFloat(dat.compare.total.v2)) /
																		parseFloat(dat.compare.total.v1)) *
																		100
															  )
															: 0
													}}
													%
												</td>
											</tr>

											<tr v-for="(list, indx) in dat.budget" :key="indx" class="opex">
												<td class="sticky-column-1"></td>
												<td class="sticky-column-2"></td>
												<td class="sticky-column-3">{{ list.name }}</td>
												<td>{{ list.description }}</td>
												<template v-for="month in months">
													<td>Rp. {{ convertNumber(list.compare[month].v1) }}</td>
													<td>Rp. {{ convertNumber(list.compare[month].v2) }}</td>
													<td>Rp. {{ convertNumber(list.compare[month].v2 - list.compare[month].v1) }}</td>
													<td>
														{{
															parseFloat(list.compare[month].v1) > 0
																? Math.round(
																		((parseFloat(list.compare[month].v2) - parseFloat(list.compare[month].v1)) /
																			parseFloat(list.compare[month].v1)) *
																			100
																  )
																: parseFloat(list.compare[month].v2) > 0
																? 100
																: 0
														}}
														%
													</td>
												</template>
												<td>Rp. {{ convertNumber(list.compare.total.v1) }}</td>
												<td>Rp. {{ convertNumber(list.compare.total.v2) }}</td>
												<td>Rp. {{ convertNumber(list.compare.total.v2 - list.compare.total.v1) }}</td>
												<td>
													{{
														parseFloat(list.compare.total.v1) > 0
															? Math.round(
																	((parseFloat(list.compare.total.v2) - parseFloat(list.compare.total.v1)) /
																		parseFloat(list.compare.total.v1)) *
																		100
															  )
															: parseFloat(list.compare.total.v2) > 0
															? 100
															: 0
													}}
													%
												</td>
												<td>
													Rp.
													{{
														parseFloat(list.compare.total.v1) > parseFloat(list.compare.total.v2)
															? convertNumber(list.compare.total.v1 - list.compare.total.v2)
															: 0
													}}
												</td>
												<td>
													{{
														parseFloat(list.compare.total.v1) > parseFloat(list.compare.total.v2)
															? Math.round(
																	((parseFloat(list.compare.total.v1) - parseFloat(list.compare.total.v2)) /
																		parseFloat(list.compare.total.v1)) *
																		100
															  )
															: 0
													}}
													%
												</td>
											</tr>
										</tbody>
									</table>
									<table v-if="filteringCapex == true" class="table table-sm p-0 table-sticky">
										<thead>
											<tr>
												<th class="text-center sticky-header-1" style="width: 150px" rowspan="3">GL</th>
												<th class="text-center sticky-header-2" style="width: 100px" rowspan="3">TT</th>
												<th class="text-center sticky-header-3" style="width: 350px" rowspan="3">Budget Item</th>
												<th class="text-center" style="width: 350px" rowspan="3">Description</th>
												<th class="text-center" style="width: 700px" colspan="4">January</th>
												<th class="text-center" style="width: 700px" colspan="4">February</th>
												<th class="text-center" style="width: 700px" colspan="4">March</th>
												<th class="text-center" style="width: 700px" colspan="4">April</th>
												<th class="text-center" style="width: 700px" colspan="4">May</th>
												<th class="text-center" style="width: 700px" colspan="4">June</th>
												<th class="text-center" style="width: 700px" colspan="4">July</th>
												<th class="text-center" style="width: 700px" colspan="4">August</th>
												<th class="text-center" style="width: 700px" colspan="4">September</th>
												<th class="text-center" style="width: 700px" colspan="4">October</th>
												<th class="text-center" style="width: 700px" colspan="4">November</th>
												<th class="text-center" style="width: 700px" colspan="4">December</th>
												<th class="text-center" style="width: 700px" colspan="4">Total</th>
												<th class="text-center" style="width: 350px" colspan="2">Savings</th>
											</tr>
											<tr>
												<template v-for="month in months">
													<th class="text-center" style="width: 250px" rowspan="2">
														Version {{ compared.version.v1 }} - {{ compared.year.v1 }}
													</th>
													<th class="text-center" style="width: 250px" rowspan="2">
														Version {{ compared.version.v2 }} - {{ compared.year.v2 }}
													</th>
													<th class="text-center" style="width: 250px" colspan="2">Deviation</th>
												</template>

												<th class="text-center" style="width: 250px" rowspan="2">
													Version {{ compared.version.v1 }} - {{ compared.year.v1 }}
												</th>
												<th class="text-center" style="width: 250px" rowspan="2">
													Version {{ compared.version.v2 }} - {{ compared.year.v2 }}
												</th>
												<th class="text-center" style="width: 250px" colspan="2">Deviation</th>
												<th class="text-center" style="width: 350px" rowspan="2">Nominal</th>
												<th class="text-center" style="width: 350px" rowspan="2">Percentage</th>
											</tr>
											<tr>
												<template v-for="month in months">
													<th class="text-center" style="width: 350px">Nominal</th>
													<th class="text-center" style="width: 350px">Percentage</th>
												</template>
												<th class="text-center" style="width: 350px">Nominal</th>
												<th class="text-center" style="width: 350px">Percentage</th>
											</tr>
										</thead>
										<tbody v-for="(dat, index) in comparedFilter" :key="index">
											<tr class="opex-first">
												<td class="bg-gray sticky-column-1">{{ dat.capex_gl.gl_code }}</td>
												<td class="bg-gray sticky-column-2">{{ dat.capex_gl.account.code }}</td>
												<td class="bg-gray sticky-column-3">{{ dat.capex_gl.account.account }}</td>
												<td class="bg-gray"></td>
												<template v-for="month in months">
													<td class="bg-gray">Rp. {{ convertNumber(dat.compare[month].v1) }}</td>
													<td class="bg-gray">Rp. {{ convertNumber(dat.compare[month].v2) }}</td>
													<td class="bg-gray">Rp. {{ convertNumber(dat.compare[month].v2 - dat.compare[month].v1) }}</td>
													<td class="bg-gray">
														{{
															parseFloat(dat.compare[month].v1) > 0
																? Math.round(
																		((parseFloat(dat.compare[month].v2) - parseFloat(dat.compare[month].v1)) /
																			parseFloat(dat.compare[month].v1)) *
																			100
																  )
																: parseFloat(dat.compare[month].v2) > 0
																? 100
																: 0
														}}
														%
													</td>
												</template>
												<td class="bg-gray">Rp. {{ convertNumber(dat.compare.total.v1) }}</td>
												<td class="bg-gray">Rp. {{ convertNumber(dat.compare.total.v2) }}</td>
												<td class="bg-gray">Rp. {{ convertNumber(dat.compare.total.v2 - dat.compare.total.v1) }}</td>
												<td class="bg-gray">
													{{
														parseFloat(dat.compare.total.v1) > 0
															? Math.round(
																	((parseFloat(dat.compare.total.v2) - parseFloat(dat.compare.total.v1)) /
																		parseFloat(dat.compare.total.v1)) *
																		100
															  )
															: parseFloat(dat.compare.total.v2) > 0
															? 100
															: 0
													}}
													%
												</td>
												<td class="bg-gray">
													Rp.
													{{
														parseFloat(dat.compare.total.v1) > parseFloat(dat.compare.total.v2)
															? convertNumber(dat.compare.total.v1 - dat.compare.total.v2)
															: 0
													}}
												</td>
												<td class="bg-gray">
													{{
														parseFloat(dat.compare.total.v1) > parseFloat(dat.compare.total.v2)
															? Math.round(
																	((parseFloat(dat.compare.total.v1) - parseFloat(dat.compare.total.v2)) /
																		parseFloat(dat.compare.total.v1)) *
																		100
															  )
															: 0
													}}
													%
												</td>
											</tr>

											<tr v-for="(list, indx) in dat.budget" :key="indx" class="opex">
												<td class="sticky-column-1"></td>
												<td class="sticky-column-2"></td>
												<td class="sticky-column-3">{{ list.name }}</td>
												<td>{{ list.description }}</td>
												<template v-for="month in months">
													<td>Rp. {{ convertNumber(list.compare[month].v1) }}</td>
													<td>Rp. {{ convertNumber(list.compare[month].v2) }}</td>
													<td>Rp. {{ convertNumber(list.compare[month].v2 - list.compare[month].v1) }}</td>
													<td>
														{{
															parseFloat(list.compare[month].v1) > 0
																? Math.round(
																		((parseFloat(list.compare[month].v2) - parseFloat(list.compare[month].v1)) /
																			parseFloat(list.compare[month].v1)) *
																			100
																  )
																: parseFloat(list.compare[month].v2) > 0
																? 100
																: 0
														}}
														%
													</td>
												</template>
												<td>Rp. {{ convertNumber(list.compare.total.v1) }}</td>
												<td>Rp. {{ convertNumber(list.compare.total.v2) }}</td>
												<td>Rp. {{ convertNumber(list.compare.total.v2 - list.compare.total.v1) }}</td>
												<td>
													{{
														parseFloat(list.compare.total.v1) > 0
															? Math.round(
																	((parseFloat(list.compare.total.v2) - parseFloat(list.compare.total.v1)) /
																		parseFloat(list.compare.total.v1)) *
																		100
															  )
															: parseFloat(list.compare.total.v2) > 0
															? 100
															: 0
													}}
													%
												</td>
												<td>
													Rp.
													{{
														parseFloat(list.compare.total.v1) > parseFloat(list.compare.total.v2)
															? convertNumber(list.compare.total.v1 - list.compare.total.v2)
															: 0
													}}
												</td>
												<td>
													{{
														parseFloat(list.compare.total.v1) > parseFloat(list.compare.total.v2)
															? Math.round(
																	((parseFloat(list.compare.total.v1) - parseFloat(list.compare.total.v2)) /
																		parseFloat(list.compare.total.v1)) *
																		100
															  )
															: 0
													}}
													%
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</b-tab>
						</b-tabs>
					</div>
				</div>
			</div>
		</div>
		<b-modal size="xl" id="modalBudget" :title="listBudget ? listBudget.opex_gl.budgetItem.budget_item : '-'" hide-footer>
			<div class="table-budget-container">
				<table class="table table-sm p-0 table-sticky">
					<thead>
						<tr>
							<th class="text-center sticky-header-1" style="width: 150px" rowspan="3">Name</th>
							<th class="text-center sticky-header-2" style="width: 300px" rowspan="3">Description</th>
							<th class="text-center" style="width: 700px" colspan="4">January</th>
							<th class="text-center" style="width: 700px" colspan="4">February</th>
							<th class="text-center" style="width: 700px" colspan="4">March</th>
							<th class="text-center" style="width: 700px" colspan="4">April</th>
							<th class="text-center" style="width: 700px" colspan="4">May</th>
							<th class="text-center" style="width: 700px" colspan="4">June</th>
							<th class="text-center" style="width: 700px" colspan="4">July</th>
							<th class="text-center" style="width: 700px" colspan="4">August</th>
							<th class="text-center" style="width: 700px" colspan="4">September</th>
							<th class="text-center" style="width: 700px" colspan="4">October</th>
							<th class="text-center" style="width: 700px" colspan="4">November</th>
							<th class="text-center" style="width: 700px" colspan="4">December</th>
							<th class="text-center" style="width: 700px" colspan="4">Total</th>
							<th class="text-center" style="width: 350px" colspan="2">Savings</th>
						</tr>
						<tr v-if="listBudget">
							<template v-for="month in months">
								<th class="text-center" style="width: 250px" rowspan="2">
									Version {{ compared.version.v1 }} - {{ compared.year.v1 }}
								</th>
								<th class="text-center" style="width: 250px" rowspan="2">
									Version {{ compared.version.v2 }} - {{ compared.year.v2 }}
								</th>
								<th class="text-center" style="width: 250px" colspan="2">Deviation</th>
							</template>

							<th class="text-center" style="width: 250px" rowspan="2">
								Version {{ compared.version.v1 }} - {{ compared.year.v1 }}
							</th>
							<th class="text-center" style="width: 250px" rowspan="2">
								Version {{ compared.version.v2 }} - {{ compared.year.v2 }}
							</th>
							<th class="text-center" style="width: 250px" colspan="2">Deviation</th>
							<th class="text-center" style="width: 350px" rowspan="2">Nominal</th>
							<th class="text-center" style="width: 350px" rowspan="2">Percentage</th>
						</tr>
						<tr>
							<template v-for="month in months">
								<th class="text-center" style="width: 350px">Nominal</th>
								<th class="text-center" style="width: 350px">Percentage</th>
							</template>
							<th class="text-center" style="width: 350px">Nominal</th>
							<th class="text-center" style="width: 350px">Percentage</th>
						</tr>
					</thead>
					<!-- {{ listBudget.plan_item_opex_id }} -->
					<tbody>
						<tr v-for="(item, index) in listBudget.budget" :key="index">
							<td class="sticky-column-1">{{ item.name }}</td>
							<td class="sticky-column-2">{{ item.description }}</td>
							<template v-for="month in months">
								<td>Rp. {{ convertNumber(item.compare[month].v1) }}</td>
								<td>Rp. {{ convertNumber(item.compare[month].v2) }}</td>
								<td>Rp. {{ convertNumber(item.compare[month].v2 - item.compare[month].v1) }}</td>
								<td>
									{{
										parseFloat(item.compare[month].v1) > 0
											? Math.round(
													((parseFloat(item.compare[month].v2) - parseFloat(item.compare[month].v1)) /
														parseFloat(item.compare[month].v1)) *
														100
											  )
											: parseFloat(item.compare[month].v2) > 0
											? 100
											: 0
									}}
									%
								</td>
							</template>
							<td>Rp. {{ convertNumber(item.compare.total.v1) }}</td>
							<td>Rp. {{ convertNumber(item.compare.total.v2) }}</td>
							<td>Rp. {{ convertNumber(item.compare.total.v2 - item.compare.total.v1) }}</td>
							<td>
								{{
									parseFloat(item.compare.total.v1) > 0
										? Math.round(
												((parseFloat(item.compare.total.v2) - parseFloat(item.compare.total.v1)) /
													parseFloat(item.compare.total.v1)) *
													100
										  )
										: parseFloat(item.compare.total.v2) > 0
										? 100
										: 0
								}}
								%
							</td>
							<td>
								Rp.
								{{
									parseFloat(item.compare.total.v1) > parseFloat(item.compare.total.v2)
										? convertNumber(item.compare.total.v1 - item.compare.total.v2)
										: 0
								}}
							</td>
							<td>
								{{
									parseFloat(item.compare.total.v1) > parseFloat(item.compare.total.v2)
										? Math.round(
												((parseFloat(item.compare.total.v1) - parseFloat(item.compare.total.v2)) /
													parseFloat(item.compare.total.v1)) *
													100
										  )
										: 0
								}}
								%
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</b-modal>
	</div>
</template>

<script>
import axios from 'axios';
import axiosIns from '@/api/axios';
import budgetItem from '@/store/modules/masterData/budget-item';
export default {
	computed: {
		budgetId() {
			return this.$route.query.id || null; // Ambil ID dari parameter URL (?id=)
		},
		detail() {
			return this.$store.getters['budgetPlan/detail'] || {};
		},
		compared() {
			return this.$store.getters['budgetPlan/compared'];
		},
		comparedFilter() {
			return this.$store.getters['budgetPlan/comparedFilter'] || [];
		},
		listTable() {
			return this.$store.getters['budgetPlan/listData'];
		},
		listDepartement() {
			return this.$store.getters['budgetPlan/listDepartment']
				.sort((a, b) => a.department_code.localeCompare(b.department_code)) // ASC sort
				.map((items) => ({
					label: `${items.department_code} - ${items.organization}`,
					value: items.id,
					row: items,
				}));
		},
	},
	data() {
		return {
			isLoading: false,
			filtering: false,
			filteringCapex: false,
			activeCapex: false,
			listBudget: '',
			budgetCompare: '',
			comparedId: '',
			selectedYear: null, // Menyimpan tahun yang dipilih
			selectedDepartment: null,
			yearOptions: [],
			formattedVersionOptions: [], // List versi berdasarkan tahun terpilih
			TTlist: {},
			budgetItemList: {},
			budgetItemCapex: [],
			TTmodel: '',
			budgetItemOpex: [],
			body: {
				pageNumber: 1,
				pageSize: 10,
				search: '',
				field: '',
				order: '',
			},
			months: ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'],
		};
	},
	async mounted() {
		// this.getList();
		// this.getDetail(); // Fetch list tahun saat komponen dimuat
		await this.getListDepartment().then(async () => {
			await this.fetchYearOptions();
		});
	},
	watch: {
		budgetId: {
			immediate: true, // Jalankan langsung saat komponen dimuat
			handler(newId) {
				if (newId) {
					this.fetchBudgetDetail(newId);
				}
			},
		},
	},
	methods: {
		// getList() {
		//   this.$store.dispatch('budgetPlan/GET_LIST_DATA', this.body)
		// },
		// Ambil daftar tahun dari API saat halaman dimuat
		async fetchYearOptions() {
			if (!this.selectedDepartment) return;
			try {
				const response = await axiosIns.get(`/plan/year/list?fundCenter=${this.selectedDepartment}`);
				const years = [];
				response.data.data.forEach((item) => {
					if (!years.includes(item.year)) {
						years.push(item.year);
					}
				});
				this.yearOptions = years.map((year) => ({ value: year, label: year }));
				this.yearOptions.sort((a, b) => b.value - a.value);
			} catch (error) {
				console.error('Failed to fetch year options:', error);
			}
		},
		async fetchVersions(selectedYear) {
			this.comparedId = null; // reset id version
			this.formattedVersionOptions = []; // reset formattedVersionOptions
			if (!this.selectedDepartment || !selectedYear) return;
			this.selectedYear = selectedYear; // Simpan tahun yang dipilih

			try {
				const response = await axiosIns.get(`/plan/year/list?year=${selectedYear}&fundCenter=${this.selectedDepartment}`);
				this.formattedVersionOptions = response.data.data.map((version) => ({
					id: version.id,
					version: `Version ${version.version} - ${version.title}`,
				}));
			} catch (error) {
				console.error('Failed to fetch version list:', error);
				this.formattedVersionOptions = [];
			}
		},
		async fetchBudgetDetail(id) {
			try {
				await this.$store.dispatch('budgetPlan/GET_BUDGET_DETAIL', id);
			} catch (error) {
				console.error('Failed to fetch budget detail:', error);
			}
		},
		getCompare(evt) {
			const id = this.$router.currentRoute.query.id;

			// Validation
			if (id === this.comparedId) {
				this.$swal({
					icon: 'error',
					title: 'Comparison Error',
					text: 'You cannot compare the same version!',
					confirmButtonColor: '#E63757',
					confirmButtonText: 'OK',
				});
				return;
			}

			this.isLoading = true;

			// Cari data berdasarkan ID yang dipilih
			const selected = this.formattedVersionOptions.find((option) => option.id === this.comparedId);
			// Jika data ditemukan, simpan ke selectedVersion
			this.selectedVersion = selected ? selected.version : null;

			let compare = {
				firstId: id,
				secondId: this.comparedId,
			};
			this.$store.dispatch('budgetPlan/GET_BUDGET_COMPARE', compare).then((resp) => {
				this.isLoading = false;
			});
		},
		detailBudget(item) {
			this.listBudget = item;
			setTimeout(() => {
				this.$bvModal.show('modalBudget');
			}, 1000);
		},
		convertNumber(dat) {
			return dat.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
		},

		compareBudget(evt) {
			this.isLoading = true;
			axiosIns.get(`${axiosIns.defaults.baseURL}/plan/${evt}`).then((resp) => {
				let data = resp.data.data;
				this.budgetCompare = data;
			});
			// this.budgetCompare = evt
		},

		selectTransactionOpex(evt, list) {
			let budget = list.find((e) => e.account_id == evt);
			const glCodes = [];
			const opexItem = [];
			budget.opex_item.forEach((item) => {
				if (!glCodes.includes(item.opex_gl.gl_code)) {
					glCodes.push(item.opex_gl.gl_code);
					opexItem.push(item);
				}
			});
			budget.opex_item = opexItem;
			this.budgetItemList = budget;
			this.budgetItemOpex = [];
		},
		selectBudgetItemOpex() {
			this.activeCapex = false;
			this.filtering = true;
			this.filteringCapex = false;
			this.isLoading = true;
			let bod = {
				firstId: this.$router.currentRoute.query.id,
				secondId: this.comparedId,
				type: 'opex',
				account_id: this.TTmodel,
				gl_code: this.budgetItemOpex,
			};
			this.$store.dispatch('budgetPlan/BUDGET_COMPARE_FILTER', bod).then((resp) => {
				this.isLoading = false;
			});
		},
		capexFilter() {
			this.activeCapex = true;
			this.filteringCapex = true;
			this.filtering = false;
			this.isLoading = true;
			let bod = {
				firstId: this.$router.currentRoute.query.id,
				secondId: this.comparedId,
				type: 'capex',
				gl_code: this.budgetItemCapex,
			};
			this.$store.dispatch('budgetPlan/BUDGET_COMPARE_FILTER', bod).then((resp) => {
				this.isLoading = false;
			});
		},
		resetFilter() {
			this.filtering = false;
			this.filteringCapex = false;
			(this.TTmodel = ''), (this.budgetItemOpex = []), (this.budgetItemCapex = []);
		},
		openOpex() {
			this.activeCapex = false;
			this.filtering = false;
			this.filteringCapex = false;
			(this.TTmodel = ''), (this.budgetItemOpex = []), (this.budgetItemCapex = []);
		},
		openCapex() {
			this.activeCapex = true;
			this.filtering = false;
			this.filteringCapex = false;
			(this.TTmodel = ''), (this.budgetItemOpex = []), (this.budgetItemCapex = []);
		},
		async getListDepartment() {
			try {
				await this.$store.dispatch('budgetPlan/GET_LIST_DEPARTMENT');
			} catch (error) {
				console.error('Failed to fetch year options:', error);
			}
		},
		async selectFundCenter(val) {
			this.selectedDepartment = val;
			this.selectedYear = null; // reset selected year
			this.comparedId = null; // reset id version
			this.formattedVersionOptions = []; // reset formattedVersionOptions
			await this.fetchYearOptions();
		},
	},
};
</script>

<style>
.bg-gray {
	background: #dad9d9 !important;
}

.tables-budget td:first-child,
.tables-budget td:nth-last-child(3),
.tables-budget td:nth-last-child(2),
.tables-budget td:last-child {
	/* text-align: center; */
}

.tables-budget > :not(caption) > * > * {
	padding: 0.5rem !important;
}

.table-bordered th,
.table-bordered td {
	border: 1px solid #c1c9cf;
}

.table-bordered th {
	color: #999d9f;
}

.table-budget-container {
	overflow: auto;
	max-height: 700px;
	border: 1px solid #c1c9cf;
	border-top: none;
}

.table-budget-container table {
	table-layout: fixed;
	word-wrap: break-word;
	width: 100%;
	border-collapse: separate;
	border-spacing: 0;
}

.table-sticky thead {
	position: sticky;
	top: 0;
	z-index: 2;
}

.table-sticky th,
.table-sticky td {
	box-shadow: inset -1px 1px 0 #ccc !important;
}

.sticky-header-1,
.sticky-header-2,
.sticky-header-3 {
	position: sticky;
	top: 0;
	z-index: 3;
	background: #f9fbfd;
}

.sticky-column-1,
.sticky-column-2,
.sticky-column-3 {
	position: sticky;
	z-index: 1;
	background: #f9fbfd !important;
	box-shadow: inset -1px 0 0 #ccc !important;
}

.sticky-header-1,
.sticky-column-1 {
	left: 0;
}

.sticky-header-2,
.sticky-column-2 {
	left: 150px;
}

.sticky-header-3,
.sticky-column-3 {
	left: 250px;
}

/* th:first-child+th+th,
    td:first-child+td+td {
      position: sticky;
      left: 0px;
      z-index: 3;
    } */

/* .opex-first th:first-child+th+th,
    .opex-first td:first-child+td+td {
      position: sticky;
      left: 0px;
      z-index: 3;
    }
    .capex th:first-child+th+th,
    .capex td:first-child+td+td {
      position: sticky;
      left: 0px;
      z-index: 3;
    }
  
    .capex-gl th:first-child+th+th,
    .capex-gl td:first-child+td+td {
      position: sticky;
      left: 0px;
      z-index: 3;
    }
  
    .capex td:first-child + td + td {
      background-color: #fff !important;
    }
  
    .opex td:first-child + td + td {
      background-color: #fff !important;
    } */
</style>
